import "../node_modules/bootstrap/dist/css/bootstrap-grid.min.css";
import "./assets/style/index.scss";

import amplitude from "amplitude-js";
import { ConnectedRouter } from "connected-react-router";
import type { CapturedNetworkRequest, CaptureResult, PostHog } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import ReactDOM from "react-dom";
import TagManager from "react-gtm-module";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { createGlobalStyle } from "styled-components";

import {
  color,
  font,
  NotoSansBoldTtf,
  NotoSansMediumTtf,
  NotoSansRegularTtf,
  resetStyles,
} from "@runwayhealth/runway-components-react";
import * as Sentry from "@sentry/react";

import App from "./App";
import { history, persistor, store } from "./store";
import { userUpdate } from "./store/user/userSlice";
import {
  applyAcquisitionParams,
  applyReferringDomain,
  harvestAcquisitionParams,
  harvestReferringDomain,
  hasActiveSession,
  hasStoredPostHogIdentity,
  readInboundDistinctId,
  readInboundSessionId,
} from "./utils/analytics/acquisitionParams";
import { isAdminAddress } from "./utils/analytics/adminPrivacy";
import { rememberArrivalFbc } from "./utils/analytics/metaClickIdentifiers";
import { ENV } from "./utils/constantsEnv";
import { runEmailHandoff } from "./utils/analytics/emailHandoff";

const environment = ENV || process.env.NODE_ENV;
const tracesSampleRate = environment === "production" ? 0.5 : 0;
const replaysSessionSampleRate = environment === "production" ? 0.5 : 0.1;

// Init Sentry
Sentry.init({
  environment,
  dsn: "https://ca23d257872e404fb3cbd0fc1df4cbd6@o1142362.ingest.sentry.io/6201327",
  integrations: [new Sentry.BrowserTracing(), new Sentry.Replay()],
  release: process.env.REACT_APP_SENTRY_RELEASE,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate,
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate,
  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: 1.0,
});

// Init amplitude
amplitude.getInstance().init("a7be61971b4c552fce287edd57348d5e");

// Init GTM
TagManager.initialize({ gtmId: "GTM-P2FW3LL" });

// Init Posthog
//
// Acquisition params (ENG-503): LPs and ads hand off to the app with
// utm_*/click-id/lp params on the URL. Harvest them at module evaluation —
// before React mounts — so no router redirect can strip them first, and
// register them as super-properties. They persist last-touch: every event
// carries them until a later campaign arrival overwrites them.
const acquisitionProps = harvestAcquisitionParams(window.location.search);

// Meta click ID: the ad arrival URL is the only place fbclid exists, so keep
// it as fbc for the server-side Conversions API events fired later in the flow.
rememberArrivalFbc();

// Referring domain (found 2026-08-03): the Payments-channel Slack destination's
// Organic Search / Direct classification reads person.$initial_referring_domain,
// which — unlike gclid/utm_source above — has no way to be registered from a
// URL param alone, because a WP -> app hop's own document.referrer only ever
// says "runwayhealth.com" by the time the app loads. WP resolves the TRUE
// external referrer on its side and forwards it as `ref_domain`; harvest that
// here, or fall back to this page's own referrer for a direct/LP arrival with
// no WP hop. Registered as a super-property below for the same reason as
// acquisitionProps: synchronous availability, no async person-merge race.
const referringDomain = harvestReferringDomain(
  window.location.search,
  document.referrer
);

// Cross-subdomain person merge (ENG-503) previously used a
// `.runwayhealth.com`-scoped PostHog cookie, but that cookie rides to every
// subdomain including the API, where its content tripped a WAF SSRF rule —
// twice (2026-06-08 and 2026-07-30). Do not reintroduce cookie persistence
// here. ENG-508 replaces it: LPs forward their PostHog distinct_id via
// `ph_distinct_id` on CTA handoff, and we adopt it below via
// `bootstrap.distinctID`, so both sides are the same person without ever
// sharing a cookie.
const inboundDistinctId = readInboundDistinctId(window.location.search);

// Session continuity (ENG-735): ENG-508 above merges *identity* across the
// LP -> app hop, but PostHog's $session_id is domain-scoped, so a brand-new
// session still starts here regardless. Forward and adopt it the same way,
// via `bootstrap.sessionID` below.
const inboundSessionId = readInboundSessionId(window.location.search);

// Fire-and-forget: not awaited against ReactDOM.render below.
runEmailHandoff({
  visitorId: inboundDistinctId,
  getCurrentEmail: () => store.getState().user.data.email,
  applyEmail: (email) => store.dispatch(userUpdate({ name: "email", value: email })),
});

const POSTHOG_KEY =
  process.env.REACT_APP_ENV === "development"
    ? "phc_to9p6iGr7nPVW8WSthR4Qz4Cag6SHWtia3Zto7yCKhx"
    : "phc_Juj8RyozDdyjOFmevbU3dw9jVoOdudoAWrxdn95eTu8";

// Adopt the inbound id only when this browser has no PostHog identity yet.
// posthog-js 1.235.2's bootstrap branch registers { distinct_id, $device_id }
// and forces USER_STATE to "anonymous" with no existing-id check, so passing
// it unconditionally would overwrite a returning visitor's distinct_id and
// orphan the person they already had. That exposure was small while only the
// LPs forwarded the param; runway-wordpress#471 extends forwarding to the
// whole WP estate, where returning customers routinely start a consultation.
const shouldAdoptInboundId =
  !!inboundDistinctId &&
  !hasStoredPostHogIdentity(POSTHOG_KEY, window.localStorage);

// Adopt the inbound session id only when this browser has no live session of
// its own. Unlike bootstrap.distinctID, posthog-js's bootstrap.sessionID path
// has no existing-session check at all — it unconditionally overwrites, so
// this guard is the only thing standing between a returning visitor's live
// app session and having it truncated mid-use by a stale LP session.
const shouldAdoptInboundSessionId =
  !!inboundSessionId &&
  !hasActiveSession(POSTHOG_KEY, window.localStorage);

const options = {
  api_host: "https://us.i.posthog.com",
  persistence: "localStorage" as const,
  ...(shouldAdoptInboundId || shouldAdoptInboundSessionId
    ? {
        bootstrap: {
          ...(shouldAdoptInboundId ? { distinctID: inboundDistinctId } : {}),
          ...(shouldAdoptInboundSessionId
            ? { sessionID: inboundSessionId }
            : {}),
        },
      }
    : {}),
  // Every event goes through here, recording snapshots ($snapshot) included, so
  // dropping them on either admin keeps patient records out of PostHog.
  before_send: (event: CaptureResult | null) =>
    isAdminAddress(window.location.pathname) ? null : event,
  loaded: (ph: PostHog) => {
    applyAcquisitionParams(ph, acquisitionProps);
    applyReferringDomain(ph, referringDomain);
  },
  session_recording: {
    maskCapturedNetworkRequestFn: (request: CapturedNetworkRequest) => {
      // The admins' requests carry patient records: record none of them.
      if (isAdminAddress(window.location.pathname)) return null;

      // Drop request/response bodies for endpoints that carry PHI/PII
      const sensitiveEndpoints = ["/v1/case", "/v1/auth", "user/"];
      const isSensitive = sensitiveEndpoints.some((ep) =>
        request.name.includes(ep)
      );

      if (isSensitive) {
        request.requestBody = undefined;
        request.responseBody = undefined;
      }

      // Strip auth tokens from headers
      if (request.requestHeaders) {
        delete request.requestHeaders["x-access-token"];
      }

      // Strip query params (may contain tokens)
      try {
        const url = new URL(request.name);
        if (url.search) {
          request.name = url.origin + url.pathname;
        }
      } catch {
        // name may not be a full URL — leave as-is
      }

      return request;
    },
  },
};

// Font files resolve through the bundler; `require` matches PaymentForm.tsx (no
// module declaration exists for .woff2).
const interBoldUrl: string = require("@fonts/Inter/Inter-Bold.woff2");
const interMediumUrl: string = require("@fonts/Inter/Inter-Medium.woff2");
const interRegularUrl: string = require("@fonts/Inter/Inter-Regular.woff2");

// Every dashboard sets its text in Inter (XCUT-R37). The component library names its
// heading faces after Gustavo (font.family.highlight), so those names load Inter too.
const GlobalStyle = createGlobalStyle`
@font-face {
  font-family: Inter-Bold;
  src: url(${interBoldUrl}) format('woff2');
  font-style: normal;
  font-weight: normal;
  font-display: swap;
}

@font-face {
  font-family: Inter-Medium;
  src: url(${interMediumUrl}) format('woff2');
  font-style: normal;
  font-weight: normal;
  font-display: swap;
}

@font-face {
  font-family: Inter-Regular;
  src: url(${interRegularUrl}) format('woff2');
  font-style: normal;
  font-weight: normal;
  font-display: swap;
}

@font-face {
  font-family: ${font.family.highlight.bold};
  src: url(${interBoldUrl}) format('woff2');
  font-style: normal;
  font-weight: normal;
  font-display: swap;
}

@font-face {
  font-family: ${font.family.highlight.medium};
  src: url(${interMediumUrl}) format('woff2');
  font-style: normal;
  font-weight: normal;
  font-display: swap;
}

@font-face {
  font-family: ${font.family.highlight.regular};
  src: url(${interRegularUrl}) format('woff2');
  font-style: normal;
  font-weight: normal;
  font-display: swap;
}

@font-face {
  font-family: ${font.family.base.bold};
  src:
  url(${NotoSansBoldTtf}) format('truetype');
  font-style: normal;
  font-weight: normal;
}

@font-face {
  font-family: ${font.family.base.medium};
  src:
  url(${NotoSansMediumTtf}) format('truetype');
  font-style: normal;
  font-weight: normal;
}

@font-face {
  font-family: ${font.family.base.regular};
  src:
  url(${NotoSansRegularTtf}) format('truetype');
  font-style: normal;
  font-weight: normal;
}

body {
  ${resetStyles}
  background-color: ${color.brand.secondary.lighter};
  font-family: Inter-Regular, ${font.family.fallback};
  -webkit-font-smoothing: antialiased;
}
input {
  background: inherit;
}
`;

ReactDOM.render(
  <PostHogProvider
    apiKey={POSTHOG_KEY}
    options={options}
  >
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ConnectedRouter history={history}>
          <GlobalStyle />
          <App />
        </ConnectedRouter>
      </PersistGate>
    </Provider>
  </PostHogProvider>,
  document.getElementById("root")
);
